import { Card, Flex, Form, Space, Tag, Upload, UploadFile } from 'antd'
import { FormInstance } from 'antd/lib'
import dayjs from 'dayjs'

import {
  InternalReturnDocumentResponse,
  InventoryDocumentState
} from 'src/api/requests'
import { CustomItemRender, Text } from 'src/components'
import { getStatus } from 'src/features/stock'
import { useStyles } from 'src/features/stock/pages/settlements/styles'
import { onDownloadFile } from 'src/hooks'
import { dayMonthYearHourMinutesFormatSeparator } from 'src/libs'
import { useLocales } from 'src/locales'

import { LocalReturnsConsumablesTable } from './LocalReturnsConsumablesTable'

type Props = {
  form: FormInstance
  item?: InternalReturnDocumentResponse
}

type Document = UploadFile & { id?: string }

export const LocalReturnDetails = ({ form, item }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles({ hasId: true })

  return (
    <Flex vertical gap={24}>
      <Flex vertical gap={24}>
        <Card>
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Flex justify="space-between">
                <Space>
                  <Text weight={400}>
                    {t('stock_applications.issuance_based_on_inquire')}:
                  </Text>
                  <Text strong>#{item?.code}</Text>
                </Space>
                <Tag
                  color={
                    getStatus(t, item?.state ?? InventoryDocumentState.NEW)
                      .color
                  }
                >
                  {getStatus(t, item?.state ?? InventoryDocumentState.NEW).text}
                </Tag>
              </Flex>

              <Space>
                <Text weight={400}>
                  {t('stock_applications.request_date')}:
                </Text>
                <Text strong>
                  {dayjs(item?.initializedOn).format(
                    dayMonthYearHourMinutesFormatSeparator
                  )}
                </Text>
              </Space>

              <Space>
                <Text weight={400}>
                  {t('stock_applications.initiated_by')}:
                </Text>
                <Text strong>
                  {item?.initializedBy?.firstName}{' '}
                  {item?.initializedBy?.lastName} -
                </Text>
                <Text strong>{item?.role?.name}</Text>
              </Space>
            </Flex>
          </Flex>
        </Card>
      </Flex>

      <Card>
        <Card title={t('stock.stocks')}>
          <Form.List name="medicine">
            {(fields, { add, remove }) => (
              <LocalReturnsConsumablesTable
                item={item}
                fields={fields}
                add={add}
                remove={remove}
                form={form}
              />
            )}
          </Form.List>
        </Card>
      </Card>

      <Card>
        <Card className={styles.detailsCard}>
          <Flex vertical gap={24}>
            <Card
              title={t('stock_applications.uploaded_documents')}
              className={styles.extraCardStyles}
            >
              <Upload
                itemRender={(_, file: Document) => (
                  <CustomItemRender
                    file={file}
                    onDownload={() => onDownloadFile(t, file?.id, file?.name)}
                  />
                )}
                defaultFileList={item?.mediaFiles?.map((file, index) => ({
                  ...file,
                  uid: String(index),
                  name: file?.name || `file-${index}`
                }))}
                disabled
              />
            </Card>

            {item?.comment && (
              <Card
                title={t('common.comment')}
                className={styles.extraCardStyles}
              >
                {item?.comment}
              </Card>
            )}
          </Flex>
        </Card>
      </Card>
    </Flex>
  )
}
